'use client';

import { c as _c } from "react/compiler-runtime";
import { datadogRum } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';
import { useEffect } from 'react';
import { selectAuthUser } from '@/features/auth/store/selectors';
import { ehTelaDoEnvioArbitral } from '@/shared/constants/publicRoutes';
import type { DatadogRumGlobalContext, DatadogRumUser } from '@/shared/types/datadogRum';
import { getDatadogRumConfig } from '@/shared/utils/datadogRumConfig';
import { redactRumEvent } from '@/shared/utils/datadogRumRedaction';
import { useAppSelector } from '@/store/hooks';
const datadogRumConfig = getDatadogRumConfig();
let isDatadogRumInitialized = false;
function mapUserToDatadogUser(user: ReturnType<typeof selectAuthUser>): DatadogRumUser | null {
  if (!user) {
    return null;
  }
  return {
    id: user.id,
    email: user.email,
    name: user.nomeCompleto,
    workspaceId: user.lastWorkspaceId ?? user.escritorioId ?? null,
    companyId: user.empresaId,
    userType: user.tipoUsuario
  };
}
function mapUserToDatadogGlobalContext(user: ReturnType<typeof selectAuthUser>): DatadogRumGlobalContext | null {
  if (!user) {
    return null;
  }
  return {
    'app.userId': user.id,
    'app.empresaId': user.empresaId,
    'app.escritorioId': user.lastWorkspaceId ?? user.escritorioId ?? null,
    'app.tipoUsuario': user.tipoUsuario
  };
}
export function DatadogRum() {
  const $ = _c(4);
  const user = useAppSelector(selectAuthUser);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  useEffect(_temp, t0);
  let t1;
  let t2;
  if ($[1] !== user) {
    t1 = () => {
      if (!datadogRumConfig || !isDatadogRumInitialized) {
        return;
      }
      const datadogUser = mapUserToDatadogUser(user);
      if (!datadogUser) {
        datadogRum.clearUser();
        datadogRum.clearGlobalContext();
        return;
      }
      const datadogGlobalContext = mapUserToDatadogGlobalContext(user);
      datadogRum.setUser(datadogUser);
      if (datadogGlobalContext) {
        datadogRum.setGlobalContext(datadogGlobalContext);
      }
    };
    t2 = [user];
    $[1] = user;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  return null;
}
function _temp() {
  if (!datadogRumConfig || isDatadogRumInitialized || ehTelaDoEnvioArbitral(location.pathname)) {
    return;
  }
  datadogRum.init({
    applicationId: datadogRumConfig.applicationId,
    clientToken: datadogRumConfig.clientToken,
    site: datadogRumConfig.site,
    service: datadogRumConfig.service,
    env: datadogRumConfig.env,
    version: datadogRumConfig.version,
    sessionSampleRate: datadogRumConfig.sessionSampleRate,
    sessionReplaySampleRate: datadogRumConfig.sessionReplaySampleRate,
    trackResources: datadogRumConfig.trackResources,
    trackUserInteractions: datadogRumConfig.trackUserInteractions,
    trackLongTasks: datadogRumConfig.trackLongTasks,
    defaultPrivacyLevel: datadogRumConfig.defaultPrivacyLevel,
    beforeSend: redactRumEvent,
    plugins: [reactPlugin({
      router: false
    })]
  });
  isDatadogRumInitialized = true;
}